/** Explicit, one-time import for the former browser-only Passport and wishlist data. */
(() => {
  const PASSPORT_PREFIX = 'furAndFarPetPassportsV3:';
  const WISHLIST_PREFIX = 'furAndFarWishlistsV1:';
  const COMPLETE_KEY = 'furAndFarLegacyImportCompleteV1';
  const DECIMAL_ID = /^[1-9][0-9]{0,18}$/;
  const DATE = /^\d{4}-\d{2}-\d{2}$/;
  const PET_TYPES = new Set(['Dog', 'Cat', 'Other companion']);
  const SIZE_CATEGORIES = new Set(['XS', 'S', 'M', 'L', 'XL']);
  const TRAVEL_STYLES = new Set(['Road trips', 'Flights', 'Camping', 'Beach escapes', 'City breaks', 'Weekend walks']);
  const TRAVEL_BEDS = new Set(['Cosy travel bed', 'Roll-up travel mat', 'Familiar blanket', 'Carrier cushion', 'No preference']);
  const PASSPORT_STATUSES = new Set(['Ready to roam', 'Planning the next trip', 'Home for now']);

  class LegacyImportError extends Error {
    constructor(code) {
      super(code);
      this.name = 'LegacyImportError';
      this.code = code;
    }
  }

  const parse = (raw, fallback) => {
    if (raw == null || raw === '') return fallback;
    try {
      return JSON.parse(raw);
    } catch (error) {
      throw new LegacyImportError('invalid_legacy_json');
    }
  };

  const requiredString = (value, maximum) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized || normalized.length > maximum) throw new LegacyImportError('invalid_legacy_data');
    return normalized;
  };

  const optionalString = (value, maximum) => {
    if (value == null || value === '') return undefined;
    return requiredString(value, maximum);
  };

  const optionalEnum = (value, allowed) => {
    if (value == null || value === '') return undefined;
    if (typeof value !== 'string' || !allowed.has(value)) throw new LegacyImportError('invalid_legacy_data');
    return value;
  };

  const optionalDecimal = (value, maximum) => {
    if (value == null || value === '') return undefined;
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0 || number > maximum) throw new LegacyImportError('invalid_legacy_data');
    return value;
  };

  const compact = (object) => Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined),
  );

  const photoCandidate = (legacyProfileId, value) => {
    const source = String(value || '').replace(/\s+/g, '');
    const match = /^data:image\/jpeg;base64,([a-z0-9+/=]+)$/i.exec(source);
    if (!match) return null;
    const bytes = Math.ceil(match[1].length * 0.75);
    if (bytes < 1 || bytes > 2_097_152) return null;
    return { legacy_profile_id: legacyProfileId, data_url: source };
  };

  const transformPet = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new LegacyImportError('invalid_legacy_data');
    const legacyProfileId = requiredString(value.id, 128);
    const petType = requiredString(value.pet_type, 40);
    if (!PET_TYPES.has(petType)) throw new LegacyImportError('invalid_legacy_data');
    const birthday = value.birthday == null || value.birthday === '' ? undefined : String(value.birthday);
    if (birthday && !DATE.test(birthday)) throw new LegacyImportError('invalid_legacy_data');
    const pet = compact({
      legacy_profile_id: legacyProfileId,
      pet_name: requiredString(value.pet_name, 40),
      pet_type: petType,
      breed: optionalString(value.breed, 60),
      birthday,
      weight_kg: optionalDecimal(value.weight_kg == null ? value.weight : value.weight_kg, 120),
      size_category: optionalEnum(value.size_category, SIZE_CATEGORIES),
      harness_size: optionalString(value.harness_size, 40),
      clothing_size: optionalString(value.clothing_size, 40),
      chest_cm: optionalDecimal(value.chest_cm, 200),
      neck_cm: optionalDecimal(value.neck_cm, 120),
      home_town: optionalString(value.home_town, 60),
      travel_style: optionalEnum(value.travel_style, TRAVEL_STYLES),
      travel_bed: optionalEnum(value.travel_bed, TRAVEL_BEDS),
      passport_status: optionalEnum(value.passport_status, PASSPORT_STATUSES),
      likes: optionalString(value.likes, 240),
      travel_notes: optionalString(value.travel_notes, 300),
    });
    return { pet, photo: photoCandidate(legacyProfileId, value.photo) };
  };

  const transformItem = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new LegacyImportError('invalid_legacy_data');
    const productId = String(value.shopify_product_id || value.id || '');
    const rawVariant = value.shopify_variant_id == null ? value.variantId : value.shopify_variant_id;
    const variantId = rawVariant == null || rawVariant === '' ? null : String(rawVariant);
    const handle = requiredString(value.product_handle || value.handle, 255);
    const productUrl = requiredString(value.product_url || value.url, 2048);
    let imageUrl = optionalString(value.image_url || value.image, 2048);
    if (imageUrl && imageUrl.startsWith('//')) imageUrl = `https:${imageUrl}`;
    if (
      !DECIMAL_ID.test(productId)
      || (variantId !== null && !DECIMAL_ID.test(variantId))
      || !/^[a-z0-9][a-z0-9-]*$/.test(handle)
      || !productUrl.startsWith('/products/')
      || (imageUrl && !imageUrl.startsWith('https://'))
    ) throw new LegacyImportError('invalid_legacy_data');
    return compact({
      shopify_product_id: productId,
      shopify_variant_id: variantId,
      product_handle: handle,
      product_title: requiredString(value.product_title || value.title, 255),
      product_url: productUrl,
      image_url: imageUrl,
      display_price: optionalString(value.display_price || value.price, 40),
    });
  };

  const uniqueItems = (items) => {
    const seen = new Set();
    const result = [];
    items.forEach((value) => {
      const item = transformItem(value);
      const key = `${item.shopify_product_id}:${item.shopify_variant_id || ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(item);
      }
    });
    return result;
  };

  const stableValue = (value) => {
    if (Array.isArray(value)) return value.map(stableValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)).map(([key, child]) => [key, stableValue(child)]));
    }
    return value;
  };

  const hashPayload = async (payload) => {
    const bytes = new TextEncoder().encode(JSON.stringify(stableValue(payload)));
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const transform = async (passportsRaw, wishlistsRaw) => {
    const legacyPets = parse(passportsRaw, []);
    const legacyWishlists = parse(wishlistsRaw, {});
    if (!Array.isArray(legacyPets) || legacyPets.length > 10 || !legacyWishlists || typeof legacyWishlists !== 'object' || Array.isArray(legacyWishlists)) {
      throw new LegacyImportError(legacyPets.length > 10 ? 'legacy_limit' : 'invalid_legacy_data');
    }
    const transformedPets = legacyPets.map(transformPet);
    const legacyIds = new Set(transformedPets.map(({ pet }) => pet.legacy_profile_id));
    const personal = legacyWishlists.personal == null ? [] : legacyWishlists.personal;
    const petLists = legacyWishlists.pets == null ? {} : legacyWishlists.pets;
    if (!Array.isArray(personal) || !petLists || typeof petLists !== 'object' || Array.isArray(petLists)) {
      throw new LegacyImportError('invalid_legacy_data');
    }
    let rawItemCount = personal.length;
    const petItems = Object.entries(petLists).map(([legacyProfileId, list]) => {
      const values = list && Array.isArray(list.items) ? list.items : null;
      if (!legacyIds.has(legacyProfileId)) throw new LegacyImportError('cross_pet_reference');
      if (!values) throw new LegacyImportError('invalid_legacy_data');
      rawItemCount += values.length;
      return { legacy_profile_id: legacyProfileId, items: uniqueItems(values) };
    });
    if (rawItemCount > 250) throw new LegacyImportError('legacy_limit');
    const payload = {
      data_collection_consent: true,
      pets: transformedPets.map(({ pet }) => pet),
      personal_items: uniqueItems(personal),
      pet_items: petItems,
    };
    const itemCount = payload.personal_items.length + payload.pet_items.reduce((sum, list) => sum + list.items.length, 0);
    return {
      payload,
      photos: transformedPets.map(({ photo }) => photo).filter(Boolean),
      summary: { pets: payload.pets.length, items: itemCount },
      importHash: await hashPayload(payload),
    };
  };

  const inspect = async ({ storage, suffix }) => {
    if (!suffix) return null;
    const passportsRaw = storage.getItem(`${PASSPORT_PREFIX}${suffix}`);
    const wishlistsRaw = storage.getItem(`${WISHLIST_PREFIX}${suffix}`);
    if (!passportsRaw && !wishlistsRaw) return null;
    return transform(passportsRaw, wishlistsRaw);
  };

  const verifyReadback = (data, transformed) => {
    if (!data || !Array.isArray(data.pets) || !Array.isArray(data.wishlists)) throw new LegacyImportError('readback_mismatch');
    const aliases = new Set(data.pets.map((pet) => pet.legacy_profile_id).filter(Boolean));
    if (!transformed.payload.pets.every((pet) => aliases.has(pet.legacy_profile_id))) {
      throw new LegacyImportError('readback_mismatch');
    }
    const itemCount = data.wishlists.reduce((sum, wishlist) => sum + (Array.isArray(wishlist.items) ? wishlist.items.length : 0), 0);
    if (itemCount < transformed.summary.items) throw new LegacyImportError('readback_mismatch');
  };

  const dataUrlBlob = (value) => {
    const encoded = value.slice(value.indexOf(',') + 1);
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: 'image/jpeg' });
  };

  const commit = async ({ api, storage, suffix }) => {
    const transformed = await inspect({ storage, suffix });
    if (!transformed) return { ok: true, skipped: true };
    await api.importLegacy(transformed.payload, transformed.importHash);
    let readback = (await api.state()).data;
    verifyReadback(readback, transformed);

    for (const photo of transformed.photos) {
      const pet = readback.pets.find((candidate) => candidate.legacy_profile_id === photo.legacy_profile_id);
      if (!pet) throw new LegacyImportError('readback_mismatch');
      const created = (await api.createPhotoUpload(pet.id, 'jpg')).data;
      await api.uploadPhoto(created.upload, dataUrlBlob(photo.data_url));
      const finalized = await api.finalizePhoto(pet.id, created.photo_id, created.path);
      if (finalized && finalized.data) readback = finalized.data;
    }

    storage.removeItem(`${PASSPORT_PREFIX}${suffix}`);
    storage.removeItem(`${WISHLIST_PREFIX}${suffix}`);
    storage.setItem(COMPLETE_KEY, 'true');
    return { ok: true, data: readback, summary: transformed.summary };
  };

  window.FurFarLegacyImport = {
    COMPLETE_KEY,
    LegacyImportError,
    commit,
    inspect,
    transform,
    verifyReadback,
  };
})();
